import React, { useEffect } from "react";
import { helmetJsonLdProp } from "react-schemaorg";

import { Container, Row, Col } from "react-bootstrap/esm/index";
import Helmet from "react-helmet";
import { pushOutboundClick } from "../../assets/datalayerFunctions";

// Import Components
import ModalLinkButton from "../../components/shared-ui/Buttons/ModalLinkButtons";
import ItpNavigation from "../../components/ITPNavigation/ITPNavigation";
import ITPHeroHeader from "../../components/HeroHeader/ITPHeroHeader";
import FooterITP from "../../components/shared-ui/Footer/Footer-ITP";
import BackToTopBtn from "../../components/shared-ui/BackToTopBtn/BackToTopBtn";
import ISIITPinline from "../../components/shared-ui/ISI/ISIITP-inline";
import ISIwrapper from "../../components/shared-ui/ISI/ISI-wrapper";
import ISIITP from "../../components/shared-ui/ISI/ISIITP";
import LookingForSupport from "../../components/shared-ui/Cards/LookingForSupport";

import PrimaryButton from "../../components/shared-ui/Buttons/PrimaryButton";

// Import Styles
import "../../css/PatientResources.scss";

function showModal(url) {
  let modal = document.querySelector("#modal");
  document.body.classList.add("no-scroll", "modal-open");
  modal.classList.remove("fadeOut");
  modal.style.display = "block";
  modal.classList.add("fadeIn");
  modal.style.height = "100%";
  modal.children[0].children[1].children[3].href = url;
  modal.children[0].children[1].children[3].target = "_blank";
  modal.children[0].children[1].children[3].addEventListener(
    "click",
    function () {
      modal.style.display = "none";
    }
  );
}

function PatientResources() {
  function addModalLinks(event) {
    pushOutboundClick(event);
    event.preventDefault();
    let url = event.currentTarget.href;
    let title = event.currentTarget.title;
    showModal(url, title);
  }
  useEffect(() => {
    window.scrollTo(0, 0);
    let location = window.location.pathname;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      page: {
        path: `${location}`,
        title: "ITP Patient Resources | DOPTELET® (avatrombopag)",
        environment: "dev",
        indication: "ITP",
        audience: "DTC",
      },
    });
  }, []);
  return (
    <div className="main_content_wrap">
      <Helmet
        script={[
          helmetJsonLdProp({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Drug",
                name: "Doptelet",
                alternateName: "avatrombopag",
                manufacturer: {
                  "@type": "Organization",
                  name: "Sobi",
                },
                dosageForm: "Pill",
              },
              {
                "@type": "WebSite",
                about: {
                  "@type": "Thing",
                  description:
                    "To inform patients of Doptelet (avatrombopag), a prescription medicine used to treat low blood platelet counts in adults with chronic immune thrombocytopenia when other treatments have not worked well enough. Doptelet (avatrombopag) is also a prescription medicine used to treat low blood platelet counts in adults with long-lasting (chronic) liver disease who are scheduled to have a medical or dental procedure.",
                },
                url: "https://doptelethcp.com/itp/itp-lifestyle-resources/",
              },
              {
                "@type": "Brand",
                logo: "https://doptelet.com/images/doptelet-avatrombopag-tablets-logo.svg",
              },
              {
                "@type": "Brand",
                logo: "https://doptelet.com/images/sobi-logo.png",
              },
              {
                "@type": "MedicalAudience",
                audienceType:
                  "Patients with chronic immune thrombocytopenia when other treatments have not worked well enough. ",
                healthCondition: {
                  "@type": "MedicalCondition",
                  alternateName: "Immune Thrombocytopenia (ITP)",
                },
                requiredMinAge: "18",
              },
              {
                "@type": "HealthTopicContent",
                hasHealthAspect:
                  "The most common side effects of DOPTELET when used to treat low blood platelet counts in adults with chronic immune thrombocytopenia (ITP) are: headache, tiredness, bruising, nosebleed, upper-respiratory tract infection, joint pain, gum bleeding, purple or red spots on your skin, runny nose",
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 3,
                    item: {
                      "@id":
                        "https://doptelet.com/support-and-resources/itp-patient-resources/",
                      Name: "Find ITP Resources",
                    },
                  },
                ],
              },
            ],
          }),
        ]}>
        <title>Chronic ITP Patient Resources | DOPTELET® (avatrombopag)</title>
        <meta
          name="description"
          content="Access patient resources to help you navigate ITP, like downloadable guides, copay assistance enrollment, tips for talking to your doctor & more."
        />
        <link
          rel="canonical"
          href="https://doptelet.com/support-and-resources/itp-patient-resources/"
        />
      </Helmet>
      <ItpNavigation />
      <div className="inner-content-wrap patient-resources">
        <ITPHeroHeader
          extraClass="large-hero inner-bg"
          markup={
            <>
              <h1>CHRONIC ITP PATIENT RESOURCES</h1>
              <p className="mb-0">
                Explore the resources below to find support for your journey with chronic ITP and more information about Doptelet.
              </p>
            </>
          }
          maxWidth="513"
          bgImage={[
            "chronic-itp-resources-hero.jpg",
            "chronic-itp-resources-hero@2x.jpg",
          ]}
          bgImageMobile={[
            "chronic-itp-resources-hero-mobile.jpg",
            "chronic-itp-resources-hero-mobile@2x.jpg",
          ]}
          imageAlt="Doptelet platelet characters getting ice cream cones at a soft serve stand in carnival scenery"
        />

        <section className="section-padding resource-cards">
          <Container>
            <Row>
              <Col>
                <h2 className="mb-4">Important Doptelet Downloads</h2>
              </Col>
            </Row>
            <Row className="mb-0">
              <Col md={12} lg={6} className="mb-4 mb-lg-0">
                <div className="cloud-blue-bg d-flex flex-column h-100">
                  <div className="d-flex align-items-center">
                    <h4><strong>Starting Doptelet?</strong></h4>
                  </div>
                  <p className="flex-grow-1">Read more about what to expect when taking Doptelet for chronic ITP.</p>
                  <PrimaryButton
                    title="DOWNLOAD BROCHURE"
                    href="/pdf/Patient-Brochure.pdf"
                    addClass="btn-primary download w-100"
                    target="_blank"
                  />
                </div>
              </Col>
              <Col md={12} lg={6} className="mb-4 mb-lg-0">
                <div className="cloud-blue-bg d-flex flex-column h-100">
                  <div className="d-flex align-items-center">
                    <h4><strong>Talking to your doctor soon?</strong></h4>
                  </div>
                  <p className="flex-grow-1">Working with your doctor can help raise your chances of finding a treatment that fits your needs and goals. Be sure to take advantage of the Doctor Discussion Guide. You can download it and print it out for support when having those important conversations with your doctor.</p>
                  <PrimaryButton
                    title="DOWNLOAD GUIDE"
                    href="/pdf/Dr-Discussion-Guide.pdf"
                    addClass="btn-primary download w-100"
                    target="_blank"
                  />
                </div>
              </Col>
              {/*<Col md={12} lg={4}>
                <div className="cloud-blue-bg d-flex flex-column h-100">
                  <div className="d-flex align-items-center">
                    <h4><strong>Think you may need financial assistance?</strong></h4>
                  </div>
                  <p className="flex-grow-1">See the options available to you, review the eligibility criteria, and enroll today.</p>
                  <PrimaryButton
                    title="DOWNLOAD FORM"
                    href="/pdf/doptelet-prescription-enrollment-form.pdf"
                    addClass="btn-primary download w-100"
                    target="_blank"
                  />
                </div>
              </Col>*/}
            </Row>
          </Container>
        </section>

        <section className="section-padding pt-0">
          <Container>
            <Row>
              <Col md={{ span: 12 }}>
                <div className="contained orange-bg d-flex align-items-center flex-column">
                  <h2>Got questions? Speak to a Doptelet Advisor</h2>
                  <p className="mb-0 text-md-center text-blue">
                    Trying to raise your low platelet count can raise some questions. The good news is that Doptelet<br className="d-none d-md-block" /> Advisors can help get you the answers you need and point you toward resources. Give them a call at<br className="d-none d-md-block" /> <a href="tel:18333682663">
                      1-833-368-2663
                    </a>.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="section-padding my-florio" style={{ background: '#00437B' }}>
          <Container>
            <Row>
              <Col md={12} lg={8} className="align-content-center">
                <h2 className="text-white">Keep your ITP journey on track with my florio ITP™</h2>
                <picture>
                  <source
                    srcSet="/images/MyFlorioLogo.png"
                    type="image/png"
                  />
                  <img
                    src="/images/MyFlorioLogo.png"
                    alt="Doptelet Florio ITP platelet tracker app icon"
                    className="d-lg-none d-block m-auto"
                  />
                </picture>
                <p className="text-white">my florio ITP™ is an all-in-one app designed to monitor all things ITP—platelet counts, bruising events, appointments, treatments, and more—plus, it’s free!</p>
                <p className="text-white">Get started with my florio ITP™ by downloading the app through the Apple Store or Google Play.</p>
                <div className="d-flex">
                  <a
                    href="https://apps.apple.com/no/app/florio-itp/id1610121401"
                    onClick={addModalLinks}
                    target="_blank"
                    className="app-link">
                    <img
                      src="/images/app-store.png"
                      alt="Apple app store icon"
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=app.florio.itp.eu.mdd&pli=1"
                    onClick={addModalLinks}
                    target="_blank"
                    className="app-link">
                    <img
                      src="/images/google-play.png"
                      alt="Google Play icon"
                    />
                  </a>
                </div>
              </Col>
              <Col md={12} lg={4} className="d-none d-lg-block">
                <picture>
                  <source
                    srcSet="/images/MyFlorioLogo.png"
                    type="image/png"
                  />
                  <img
                    src="/images/MyFlorioLogo.png"
                    alt="Doptelet Florio ITP platelet tracker app icon"
                    className="m-auto d-none d-md-block"
                  />
                </picture>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="section-padding resource-cards cloud-blue-bg organizations">
          <Container>
            <Row>
              <Col className="col col-md-9">
                <img
                  src="/images/swings_transparent.png"
                  className="mb-4"
                  alt="Doptelet carnival swing ride"
                />
                <h2>Join Support Groups for Chronic ITP</h2>
                <p className="mb-3">If you’re looking to meet others living with platelet conditions like chronic immune thrombocytopenia, these organizations are good places to start. They exist to create a community of support and information for people living with ITP.</p>
              </Col>
            </Row>
            <Row className="mb-0">
              <Col md={12} lg={4} className="mb-4 mb-lg-0">
                <div className="cloud-blue-bg d-flex flex-column h-100 org">
                  <div className="org-wrapper">
                    <img
                      src="/images/platelet-disorder-support-assocation-pdsa-logo-empowering-itp-patients.png"
                      alt="Platelet Disorder Support Association (PDSA) - Empowering ITP Patients Logo"
                      className="mb-4"
                    />
                    <h3><strong>Platelet Disorder Support Association (PDSA)</strong></h3>
                    <p className="flex-grow-1">A non-profit organization founded in 1998 to enhance the lives of patients with ITP and other platelet disorders through education, advocacy, research, and support.</p>
                    <ModalLinkButton
                      title="Visit PDSA"
                      href="https://www.pdsa.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      addClass="modalLink btn-white btn-external-link"
                      onClick={addModalLinks}
                    />
                  </div>
                </div>
              </Col>
              <Col md={12} lg={4} className="mb-4 mb-lg-0">
                <div className="cloud-blue-bg d-flex flex-column h-100 org">
                  <div className="org-wrapper">
                    <img
                      src="/images/foundation-for-women-and-girls-blood-disorders-logo.png"
                      alt="Foundation for Women & Girls with Blood Disorders Logo"
                      className="mb-4"
                    />
                    <h3><strong>Foundation for Women & Girls with Blood Disorders (FWGBD)</strong></h3>
                    <p className="flex-grow-1">An organization that helps to ensure that all women with blood disorders are correctly diagnosed and treated at every stage of life.</p>
                    <ModalLinkButton
                      title="VISIT FWGBD"
                      href="https://www.fwgbd.org/patient"
                      target="_blank"
                      rel="noopener noreferrer"
                      addClass="modalLink btn-white btn-external-link"
                      onClick={addModalLinks}
                    />
                  </div>
                </div>
              </Col>
              <Col md={12} lg={4}>
                <div className="cloud-blue-bg d-flex flex-column h-100 org">
                  <div className="org-wrapper">
                    <img
                      src="/images/national-organization-for-rare-disorders-nord-logo.png"
                      alt="National Organization for Rare Disorders (NORD) Logo"
                      className="mb-4"
                    />
                    <h3><strong>National Organization for Rare Disorders (NORD)</strong></h3>
                    <p className="flex-grow-1">A non-profit organization for people living with rare diseases. NORD is committed to the identification, treatment, and cure of rare diseases.</p>
                    <ModalLinkButton
                      title="VISIT NORD"
                      href="https://rarediseases.org/rare-diseases/immune-thrombocytopenia/"
                      target="_blank"
                      rel="noopener noreferrer"
                      addClass="modalLink btn-white btn-external-link"
                      onClick={addModalLinks}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <LookingForSupport addClass="section-padding" />

        <ISIITPinline />
      </div>
      <FooterITP />
      <ISIwrapper>
        <ISIITP />
      </ISIwrapper>
      <BackToTopBtn />
    </div>
  );
}

export default PatientResources;
